import { useMemo, useState } from "react";

import { SectionScoreCard } from "../SectionScoreCard";

import { SectionAssessmentStudentView } from "./SectionAssessmentStudentView";
import { SectionAssessmentView } from "./SectionAssessmentView";

import { t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { IconText } from "mds/components/IconText";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { PersonClassIcon, PersonSingleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentSection,
  selectCurrentUserId,
  selectUsersWhoCanParticipateInSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

const InstructorAssessmentView = ({ sectionScoreEnabled }: { sectionScoreEnabled: boolean }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const studentUsers = useListSelector(selectUsersWhoCanParticipateInSection, PROFILE_PROPERTIES);
  const selectedUser = studentUsers.find((u) => u.id === selectedUserId) || null;

  const options = useMemo(() => {
    return [
      {
        title: t("assessment_dashboard.all.dropdown_all_students"),
        id: "",
        addDivider: true,
      },
      ...studentUsers
        .map((user) => ({
          title: getUserFullName(user),
          id: user.id,
        }))
        .toSorted(ORDER_BY_TITLE),
    ];
  }, [studentUsers]);

  const sectionScores = sectionScoreEnabled ? <SectionScoreCard userId={selectedUserId} /> : null;

  const userPicker = (
    <DropdownMenu
      buttonText={
        <IconText
          iconStart={selectedUserId ? <PersonSingleIcon /> : <PersonClassIcon />}
          text={
            selectedUser
              ? getUserFullName(selectedUser)
              : t("assessment_dashboard.all.dropdown_all_students")
          }
        />
      }
      items={options}
      selectedValue={selectedUserId || ""}
      size="s"
      onSelectionChange={(item) => {
        const userId = item.id === "" ? null : item.id;
        setSelectedUserId(userId);
      }}
    />
  );

  const assessmentView = selectedUserId ? (
    <SectionAssessmentStudentView userId={selectedUserId} />
  ) : (
    <SectionAssessmentView />
  );

  return (
    <>
      {userPicker}
      {sectionScores}
      {assessmentView}
    </>
  );
};

const StudentAssessmentView = ({
  sectionScoreEnabled,
  userId,
}: {
  sectionScoreEnabled: boolean;
  userId: string;
}) => {
  const sectionScores = sectionScoreEnabled ? <SectionScoreCard userId={userId} /> : null;

  return (
    <>
      {sectionScores}
      <SectionAssessmentStudentView userId={userId} />
    </>
  );
};

export const SectionAssessmentMainView = () => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const section = useAppSelector(selectCurrentSection);
  const sectionScoreEnabled = section?.section_score_enabled;
  const { canParticipateInSection, canAuthorSection } = useCapabilities();

  if (sectionScoreEnabled === undefined || !currentUserId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {canParticipateInSection ? (
        <StudentAssessmentView sectionScoreEnabled={sectionScoreEnabled} userId={currentUserId} />
      ) : canAuthorSection ? (
        <InstructorAssessmentView sectionScoreEnabled={sectionScoreEnabled} />
      ) : sectionScoreEnabled ? (
        <SectionScoreCard />
      ) : null}
    </div>
  );
};
